import { z } from "zod";

/**
 * Publicação agendada / publicada (multi-rede).
 */

export const tipoDePublicacaoSchema = z.enum(["imagem", "carrossel", "reels"]);

export const plataformaDePublicacaoSchema = z.enum([
  "instagram",
  "facebook",
  "twitter",
  "tiktok",
]);

export const statusDePublicacaoSchema = z.enum([
  "rascunho",
  "aprovado",
  "queued",
  "scheduled",
  "publicado",
  "falhou",
]);

export const midiaDaPublicacaoSchema = z.object({
  id: z.string().uuid(),
  objectKey: z.string().min(1),
  mimeType: z.string().min(1),
  ordem: z.number().int().nonnegative(),
  larguraPx: z.number().int().positive().nullable(),
  alturaPx: z.number().int().positive().nullable(),
  duracaoSegundos: z.number().int().nonnegative().nullable(),
  thumbnailKey: z.string().nullable(),
  previewUrl: z.string().nullable().optional(),
});

export const publicacaoSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  nome: z.string(),
  legenda: z.string(),
  tipo: tipoDePublicacaoSchema,
  plataforma: plataformaDePublicacaoSchema,
  status: statusDePublicacaoSchema,
  agendarPara: z.string().datetime().nullable(),
  publicadoEm: z.string().datetime().nullable(),
  metaContainerId: z.string().nullable(),
  /** Permalink na rede (legado: Instagram; vale para X/TikTok também). */
  instagramUrl: z.string().nullable(),
  clickupTaskId: z.string().nullable(),
  motivoFalha: z.string().nullable(),
  tentativas: z.number().int().nonnegative(),
  midias: z.array(midiaDaPublicacaoSchema),
  previewUrl: z.string().nullable().optional(),
  /** Usernames convidados pro collab post (so Instagram; ate 3). */
  colaboradores: z.array(z.string()).default([]),
  criadoEm: z.string().datetime(),
  atualizadoEm: z.string().datetime(),
});

export const listaDePublicacoesSchema = z.object({
  itens: z.array(publicacaoSchema),
});

export const MAX_COLABORADORES_POST = 3;

export const criarPublicacaoSchema = z.object({
  nome: z.string().trim().min(2).max(120),
  legenda: z.string().max(2200).default(""),
  tipo: tipoDePublicacaoSchema,
  plataforma: plataformaDePublicacaoSchema.default("instagram"),
  /** Atalho: cria uma publicação por plataforma (mesmo conteúdo). */
  plataformas: z
    .array(plataformaDePublicacaoSchema)
    .min(1)
    .max(4)
    .optional(),
  /** Usernames do Instagram convidados como collab (so faz sentido pra Instagram). */
  colaboradores: z
    .array(z.string().trim().min(1).max(30))
    .max(MAX_COLABORADORES_POST)
    .default([]),
});

export const agendarPublicacaoSchema = z.object({
  agendarPara: z.string().datetime({ offset: true }),
});

/** Edição parcial enquanto o post não foi publicado. Com data, volta para a fila. */
export const editarPublicacaoSchema = z.object({
  nome: z.string().trim().min(2).max(120).optional(),
  legenda: z.string().max(2200).optional(),
  tipo: tipoDePublicacaoSchema.optional(),
  colaboradores: z
    .array(z.string().trim().min(1).max(30))
    .max(MAX_COLABORADORES_POST)
    .optional(),
  agendarPara: z.string().datetime({ offset: true }).optional(),
});

export const copiarMidiasDePublicacaoSchema = z.object({
  origemId: z.string().uuid(),
});

export const contagemFalhasPublicacaoSchema = z.object({
  total: z.number().int().nonnegative(),
});

export const exclusaoPublicacaoSchema = z.object({
  ok: z.literal(true),
});

export const statusFilaPublicacoesSchema = z.object({
  waiting: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  delayed: z.number().int().nonnegative(),
});

export const contaSocialSchema = z.object({
  plataforma: plataformaDePublicacaoSchema,
  status: z.enum(["conectado", "nao_conectado", "expirado"]),
  contaExternaId: z.string().nullable(),
  username: z.string().nullable(),
  nomeExibicao: z.string().nullable(),
  fotoUrl: z.string().nullable(),
  tokenExpiraEm: z.string().datetime().nullable(),
  /** oauth: app oficial. sessao: login no site. null: conta antiga. */
  modo: z.enum(["oauth", "sessao"]).nullable(),
  /** Números públicos lidos no login (hoje só TikTok). null quando não há. */
  perfil: z
    .object({
      bio: z.string().nullable(),
      verificado: z.boolean(),
      seguidores: z.number().nullable(),
      seguindo: z.number().nullable(),
      curtidas: z.number().nullable(),
      videos: z.number().nullable(),
    })
    .nullable()
    .optional(),
});

/** QR aberto: imagem (data URL PNG) para a pessoa escanear com o app. */
export const loginTikTokIniciadoSchema = z.object({
  desafioId: z.string().uuid(),
  imagemQr: z.string().min(1),
});

/** Situacao do login por QR enquanto a pessoa nao confirma no celular. */
export const opcoesIdentidadeTikTokSchema = z.object({
  email: z.boolean(),
  sms: z.boolean(),
  senha: z.boolean(),
  pedeCodigo: z.boolean(),
  /** Contato mascarado que o TikTok mostra em cada opção (ex.: +55 ****6992). */
  contatos: z
    .object({ sms: z.string().optional(), email: z.string().optional() })
    .optional(),
});

export const statusLoginTikTokSchema = z.discriminatedUnion("tipo", [
  z.object({
    tipo: z.literal("aguardando"),
    imagemQr: z.string().min(1),
    /** qr: código para escanear. identidade: TikTok pediu senha, e-mail ou SMS. */
    etapa: z.enum(["qr", "identidade"]),
    opcoes: opcoesIdentidadeTikTokSchema,
  }),
  z.object({
    tipo: z.literal("conectada"),
    username: z.string().min(1),
  }),
  z.object({
    tipo: z.literal("expirado"),
  }),
]);

export const listaContasSociaisSchema = z.object({
  contas: z.array(contaSocialSchema),
});

/** Preview do perfil pra conferir quem entra no collab. */
export const perfilInstagramSchema = z.discriminatedUnion("encontrado", [
  z.object({
    encontrado: z.literal(true),
    username: z.string().min(1),
    nome: z.string().nullable(),
    fotoUrl: z.string().nullable(),
  }),
  z.object({
    encontrado: z.literal(false),
    username: z.null(),
    nome: z.null(),
    fotoUrl: z.null(),
  }),
]);

export type TipoDePublicacaoDto = z.infer<typeof tipoDePublicacaoSchema>;
export type PlataformaDePublicacaoDto = z.infer<
  typeof plataformaDePublicacaoSchema
>;
export type StatusDePublicacaoDto = z.infer<typeof statusDePublicacaoSchema>;
export type PublicacaoDto = z.infer<typeof publicacaoSchema>;
export type ListaDePublicacoesDto = z.infer<typeof listaDePublicacoesSchema>;
export type CriarPublicacaoDto = z.infer<typeof criarPublicacaoSchema>;
export type AgendarPublicacaoDto = z.infer<typeof agendarPublicacaoSchema>;
export type EditarPublicacaoDto = z.infer<typeof editarPublicacaoSchema>;
export type StatusFilaPublicacoesDto = z.infer<
  typeof statusFilaPublicacoesSchema
>;
export type ContaSocialDto = z.infer<typeof contaSocialSchema>;
export type LoginTikTokIniciadoDto = z.infer<typeof loginTikTokIniciadoSchema>;
export type StatusLoginTikTokDto = z.infer<typeof statusLoginTikTokSchema>;
export type PerfilInstagramDto = z.infer<typeof perfilInstagramSchema>;
